import { useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, type AdminReport } from "@/lib/api";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable } from "@/components/ui";
import { Button } from "@/components/ui/button";
import type { ColumnDef, RowAction, BulkAction } from "@/components/ui";
import { Trash2, RotateCcw, Download, Clock, Pencil, FileCode } from "lucide-react";
import { AdminReportDetailModal } from "./AdminReportDetailModal";
import { downloadCsv } from "@/lib/utils";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

const STATUS_OPTIONS = [
  { value: "", label: "Alla statusar" },
  { value: "draft", label: "Utkast" },
  { value: "in_progress", label: "Pågående" },
  { value: "completed", label: "Klar" },
  { value: "exported", label: "Exporterad" },
  { value: "finalized", label: "Skickad till Bolagsverket" },
];

const VISIBILITY_OPTIONS = [
  { value: "active", label: "Aktiva" },
  { value: "all", label: "Alla" },
];

const STEP_OPTIONS = [
  { value: "", label: "Alla steg" },
  { value: "1", label: "1/5" },
  { value: "2", label: "2/5" },
  { value: "3", label: "3/5" },
  { value: "4", label: "4/5" },
  { value: "5", label: "5/5" },
];

const statusBadgeStyles: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  exported: "bg-purple-100 text-purple-700",
  finalized: "bg-primary-100 text-primary-700",
};

const statusLabels: Record<string, string> = {
  draft: "Utkast",
  in_progress: "Pågående",
  completed: "Klar",
  exported: "Exporterad",
  finalized: "Skickad till Bolagsverket",
};

function formatFiscalYear(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  if (s.getMonth() === 0 && e.getMonth() === 11) {
    return `${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString("sv-SE")} - ${e.toLocaleDateString("sv-SE")}`;
}

// Default retention period (can be changed in admin settings)
const DEFAULT_RETENTION_DAYS = 30;

/**
 * Calculate days remaining until permanent deletion
 */
function getDaysUntilPermanentDelete(deletedAt: string | null | undefined): number | null {
  if (!deletedAt) return null;
  const deletedDate = new Date(deletedAt);
  const permanentDeleteDate = new Date(deletedDate);
  permanentDeleteDate.setDate(permanentDeleteDate.getDate() + DEFAULT_RETENTION_DAYS);
  const now = new Date();
  const diffMs = permanentDeleteDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

export function AdminReports() {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [editReportId, setEditReportId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    refresh,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
  } = useDataTable<AdminReport, { status: string; visibility: string; fiscalYear: string; currentStep: string; createdAt: string }>({
    fetcher: (params) =>
      adminApi.listReports({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        status: params.status || undefined,
        includeDeleted: params.visibility === "all" || undefined,
        fiscalYear: params.fiscalYear || undefined,
        currentStep: params.currentStep || undefined,
        createdAfter: params.createdAt || undefined,
        createdBefore: params.createdAt || undefined,
      }),
    pageSize: 25,
    initialFilters: { status: "", visibility: "active", fiscalYear: "", currentStep: "", createdAt: "" },
    sortKeyMap: {
      company: "companyName",
      fiscalYear: "fiscalYearEnd",
      status: "status",
      step: "currentStep",
      createdAt: "createdAt",
    },
  });

  const handleDelete = useCallback(
    async (report: AdminReport) => {
      if (!confirm("Är du säker på att du vill ta bort denna årsredovisning?")) return;
      try {
        const response = await adminApi.deleteReport(report.id);
        if (response.success) {
          refresh();
          toast.success("Årsredovisningen har tagits bort");
        }
      } catch {
        toast.error("Kunde inte ta bort årsredovisningen");
      }
    },
    [refresh]
  );

  const handleRestore = useCallback(
    async (report: AdminReport) => {
      try {
        const response = await adminApi.restoreReport(report.id);
        if (response.success) {
          refresh();
          toast.success("Årsredovisningen har återställts");
        }
      } catch {
        toast.error("Kunde inte återställa årsredovisningen");
      }
    },
    [refresh]
  );

  const handleDownloadXbrl = useCallback(async (report: AdminReport) => {
    const toastId = toast.loading("Genererar iXBRL...");
    try {
      const result = await adminApi.downloadReportXbrl(report.id);
      if (result.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename || `arsredovisning-${report.organizationNumber || report.companyId}.xhtml`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("iXBRL har laddats ner", { id: toastId });
      } else {
        toast.error(result.error || "Kunde inte generera iXBRL", { id: toastId });
      }
    } catch {
      toast.error("Ett fel uppstod vid generering av iXBRL", { id: toastId });
    }
  }, []);

  const columns: ColumnDef<AdminReport>[] = useMemo(
    () => [
      {
        id: "company",
        header: "Företag",
        sortable: true,
        sortAccessor: (row) => row.companyName || row.companyId,
        filter: {
          type: "text" as const,
          value: search,
          onChange: setSearch,
          placeholder: "Sök namn eller org.nr...",
        },
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-gray-900">
              {row.companyName || row.companyId}
            </p>
            {row.organizationNumber && (
              <p className="text-xs text-gray-500">{row.organizationNumber}</p>
            )}
          </div>
        ),
      },
      {
        id: "fiscalYear",
        header: "Räkenskapsår",
        sortable: true,
        sortAccessor: (row) => row.fiscalYearEnd,
        filter: {
          type: "text" as const,
          value: filters.fiscalYear,
          onChange: (v: string) => setFilter("fiscalYear", v),
          placeholder: "Sök år...",
        },
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {formatFiscalYear(row.fiscalYearStart, row.fiscalYearEnd)}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        sortAccessor: (row) => row.status,
        filter: {
          type: "select" as const,
          value: filters.status,
          onChange: (v: string) => setFilter("status", v),
          options: STATUS_OPTIONS,
        },
        cell: (row) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeStyles[row.status] || "bg-gray-100 text-gray-700"}`}
          >
            {statusLabels[row.status] || row.status}
          </span>
        ),
      },
      {
        id: "step",
        header: "Steg",
        hideBelow: "sm",
        sortable: true,
        sortAccessor: (row) => row.currentStep,
        filter: {
          type: "select" as const,
          value: filters.currentStep,
          onChange: (v: string) => setFilter("currentStep", v),
          options: STEP_OPTIONS,
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">{row.currentStep}/5</span>
        ),
      },
      {
        id: "createdAt",
        header: "Skapad",
        hideBelow: "md",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        filter: {
          type: "date" as const,
          value: filters.createdAt,
          onChange: (v: string) => setFilter("createdAt", v),
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleDateString("sv-SE")}
          </span>
        ),
      },
      {
        id: "visibility",
        header: "Synlighet",
        hideBelow: "lg",
        sortable: true,
        sortAccessor: (row) => (row.deletedAt ? 1 : 0),
        filter: {
          type: "select" as const,
          value: filters.visibility,
          onChange: (v: string) => setFilter("visibility", v),
          options: VISIBILITY_OPTIONS,
        },
        cell: (row) => {
          if (row.deletedAt) {
            const daysRemaining = getDaysUntilPermanentDelete(row.deletedAt);
            return (
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <Trash2 size={12} />
                  Borttagen
                </span>
                {daysRemaining !== null && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-red-600">
                    <Clock size={10} />
                    {daysRemaining === 0
                      ? "Raderas snart"
                      : daysRemaining === 1
                        ? "Raderas om 1 dag"
                        : `Raderas om ${daysRemaining} dagar`}
                  </span>
                )}
              </div>
            );
          }
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Aktiv
            </span>
          );
        },
      },
    ],
    [search, setSearch, filters.fiscalYear, filters.currentStep, filters.createdAt, filters.status, filters.visibility, setFilter]
  );

  const rowActions: RowAction<AdminReport>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Redigera",
        icon: Pencil,
        onClick: (row) => setEditReportId(row.id),
        visible: (row) => !row.deletedAt,
      },
      {
        key: "download-xbrl",
        label: "Exportera iXBRL",
        icon: FileCode,
        onClick: handleDownloadXbrl,
        visible: (row) =>
          !row.deletedAt &&
          ["completed", "exported", "finalized"].includes(row.status),
      },
      {
        key: "restore",
        label: "Återställ",
        icon: RotateCcw,
        variant: "success",
        onClick: handleRestore,
        visible: (row) => !!row.deletedAt,
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleDelete,
        visible: (row) => !row.deletedAt,
      },
    ],
    [handleDelete, handleRestore, handleDownloadXbrl]
  );

  // Bulk actions
  const handleBulkDelete = useCallback(
    async (reports: AdminReport[]) => {
      const deletableReports = reports.filter((r) => !r.deletedAt);
      if (deletableReports.length === 0) {
        toast.error("Inga årsredovisningar kan tas bort");
        return;
      }
      if (
        !confirm(
          `Är du säker på att du vill ta bort ${deletableReports.length} årsredovisningar?`
        )
      ) {
        return;
      }
      setIsBulkDeleting(true);
      try {
        let successCount = 0;
        let errorCount = 0;
        for (const report of deletableReports) {
          try {
            const response = await adminApi.deleteReport(report.id);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch {
            errorCount++;
          }
        }
        if (successCount > 0) {
          toast.success(`${successCount} årsredovisningar har tagits bort`);
          setSelectedKeys(new Set());
          refresh();
        }
        if (errorCount > 0) {
          toast.error(`Kunde inte ta bort ${errorCount} årsredovisningar`);
        }
      } finally {
        setIsBulkDeleting(false);
      }
    },
    [refresh]
  );

  const handleBulkExport = useCallback((reports: AdminReport[]) => {
    downloadCsv(
      reports,
      [
        { header: "ID", accessor: (r) => r.id },
        { header: "Företag", accessor: (r) => r.companyName || r.companyId },
        { header: "Org.nummer", accessor: (r) => r.organizationNumber || "" },
        { header: "Räkenskapsår start", accessor: (r) => r.fiscalYearStart },
        { header: "Räkenskapsår slut", accessor: (r) => r.fiscalYearEnd },
        { header: "Status", accessor: (r) => statusLabels[r.status] || r.status },
        { header: "Steg", accessor: (r) => `${r.currentStep}/5` },
        { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
        { header: "Aktiv", accessor: (r) => (r.deletedAt ? "Borttagen" : "Aktiv") },
      ],
      `arsredovisningar-urval-${new Date().toISOString().slice(0, 10)}.csv`
    );
    toast.success(`${reports.length} årsredovisningar exporterade`);
  }, []);

  const bulkActions: BulkAction<AdminReport>[] = useMemo(
    () => [
      {
        key: "export",
        label: "Exportera",
        icon: Download,
        onClick: handleBulkExport,
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleBulkDelete,
        loading: isBulkDeleting,
      },
    ],
    [handleBulkExport, handleBulkDelete, isBulkDeleting]
  );

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.reports.title} noIndex />
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Årsredovisningar</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Alla årsredovisningar i systemet
            </p>
          </div>
        <Button
          className="w-full sm:w-auto"
          variant="outline"
          size="sm"
          onClick={() =>
            downloadCsv(
              data,
              [
                { header: "ID", accessor: (r) => r.id },
                { header: "Företag", accessor: (r) => r.companyName || r.companyId },
                { header: "Org.nummer", accessor: (r) => r.organizationNumber || "" },
                { header: "Räkenskapsår start", accessor: (r) => r.fiscalYearStart },
                { header: "Räkenskapsår slut", accessor: (r) => r.fiscalYearEnd },
                { header: "Status", accessor: (r) => statusLabels[r.status] || r.status },
                { header: "Steg", accessor: (r) => `${r.currentStep}/5` },
                { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
                { header: "Aktiv", accessor: (r) => (r.deletedAt ? "Borttagen" : "Aktiv") },
              ],
              `arsredovisningar-${new Date().toISOString().slice(0, 10)}.csv`
            )
          }
          disabled={data.length === 0}
        >
          <Download size={14} />
          Exportera CSV
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        emptyMessage="Inga årsredovisningar hittades"
        pagination={pagination}
        onPageChange={setPage}
        pageSizeOptions={[25, 50, 100, 250]}
        onPageSizeChange={setPageSize}
        rowActions={rowActions}
        rowClassName={(row) => (row.deletedAt ? "bg-red-50/50 hover:bg-red-100/50" : undefined)}
        serverSortColumnId={sortColumnId}
        serverSortDirection={sortDirection}
        onSort={handleSort}
        onResetFilters={resetFilters}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        bulkActions={bulkActions}
      />
    </div>

    {/* Edit Report Modal */}
    {editReportId && (
      <AdminReportDetailModal
        reportId={editReportId}
        onClose={() => setEditReportId(null)}
        onUpdated={refresh}
      />
    )}
    </>
  );
}
